/**
 * Data Pipeline Dashboard
 * 
 * Unified dashboard combining the data processing funnel:
 * Sources → Knowledge Base → Jobs → Entities → Chat
 * 
 * This represents the complete data lifecycle from ingestion to AI-powered insights.
 */

import React, { useState } from 'react';
import { Tabs } from '../components/UI/Tabs';
import { PageHeader } from '../components/UI/PageHeader';
import { PageContainer } from '../components/UI/PageContainer';
import { ProtectedRoute } from '../components/Auth/ProtectedRoute';
import { Database, Package, Zap, Grid, MessageSquare } from 'lucide-react';
import { SourceList } from '../components/Sources/SourceList';
import { KnowledgeBase } from '../components/Knowledge/KnowledgeBase';
import { JobList } from '../components/Jobs/JobList';
import { ChatInterface } from '../components/RAG/ChatInterface';
import { EntityExplorer } from '../components/Entities/EntityExplorer';

export function DataManagementDashboard() {
  // Get tab from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const defaultTab = urlParams.get('tab') || 'sources';
  
  // Update URL when tab changes
  const handleTabChange = (tabId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabId);
    window.history.pushState({}, '', url.toString());
  };
  
  return (
    <ProtectedRoute requiredPermission="data_management.read">
      <PageContainer>
        <PageHeader
          icon={<Database />}
          title="Data Pipeline"
          subtitle="Complete data lifecycle: from source ingestion to AI-powered insights"
        />
        
        {/* Funnel Flow Indicator */}
        <div className="flex items-center gap-2 text-sm text-theme-text-secondary mb-6">
          <div className="flex items-center gap-1">
            <Database className="h-4 w-4" />
            <span>Sources</span>
          </div>
          <span>→</span>
          <div className="flex items-center gap-1">
            <Package className="h-4 w-4" />
            <span>Knowledge</span>
          </div>
          <span>→</span>
          <div className="flex items-center gap-1">
            <Zap className="h-4 w-4" />
            <span>Jobs</span>
          </div>
          <span>→</span>
          <div className="flex items-center gap-1">
            <Grid className="h-4 w-4" />
            <span>Entities</span>
          </div>
          <span>→</span>
          <div className="flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            <span>Chat</span>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs
          tabs={[
            {
              id: 'sources',
              label: 'Sources',
              icon: <Database className="h-4 w-4" />,
              description: 'Upload and manage data sources',
              content: <SourceList />,
            },
            {
              id: 'knowledge',
              label: 'Knowledge Base',
              icon: <Package className="h-4 w-4" />,
              description: 'Browse artifacts and chunks',
              content: <KnowledgeBase />,
            },
            {
              id: 'jobs',
              label: 'Processing Jobs',
              icon: <Zap className="h-4 w-4" />,
              description: 'Track ingestion and processing',
              content: <JobList />,
            },
            {
              id: 'entities',
              label: 'Entities',
              icon: <Grid className="h-4 w-4" />,
              description: 'Explore extracted structured data',
              content: <EntityExplorer />,
            },
            {
              id: 'chat',
              label: 'AI Chat',
              icon: <MessageSquare className="h-4 w-4" />,
              description: 'Query your data with AI',
              content: <ChatInterface />,
            },
          ]}
          defaultTab={defaultTab}
          onChange={handleTabChange}
        />
      </PageContainer>
    </ProtectedRoute>
  );
}

